"use client";

import { useEffect, useRef, useState } from "react";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import { Switch, message } from "antd";
import PropTypes from "prop-types";

import Sidebar from "../Sidebar";
import {
  addPrintSettings,
  getPrintSettings,
} from "../../services/doctor/print_settings/printSettings";
import { useAuth } from "../../services/authentication_API/authService";
import { useMessageApi } from "../../services/message api/MessageProvider";
import SignatureCanvas from "react-signature-canvas";

export default function PrintSettings() {
  const [settings, setSettings] = useState({
    language: "English",
    fontFamily: "Arial, Helvetica, sans-serif",
    fontSize: 14,
    footerAlign: "right",
    template: "Single Column",
    medicineTemplate: "Table",
    investigationResultTemplate: "Table",
    investigationTemplate: "Numbered List",
    letterhead: false,
    signature: true,
    margins: { top: 2, right: 1.5, bottom: 2, left: 1.5 }, // cm
  });
  const [loading, setLoading] = useState(false);
  const { userData } = useAuth();
  const messageApi = useMessageApi();
  const sigCanvas = useRef();
    const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // 🟢 Only run when signature changes and ref is ready
    if (settings?.signature && sigCanvas.current && !isLoaded) {
      const img = new Image();
      img.crossOrigin = "anonymous"; // allow CORS for remote image
      img.src = settings.signature;

      img.onload = () => {
        try {
          sigCanvas.current.fromDataURL(img.src);
          setIsLoaded(true);
        } catch (err) {
          console.error("Failed to load signature:", err);
        }
      };
    }
  }, [settings?.signature, isLoaded]);
  const clearSignature = () => {
    sigCanvas.current.clear();
    setSettings((prev) => ({ ...prev, signature: "" }));
  };

const saveSignature = () => {
  if (!sigCanvas.current.isEmpty()) {
    const data = sigCanvas.current.toDataURL("image/png");
    setSettings(prev => ({ ...prev, signature: data }));
  } else {
    alert("Please draw your signature before saving.");
  }
};

  useEffect(() => {
    const sidebar = document.getElementById("sidebar");
    if (sidebar) {
      sidebar.classList.add("sidebar-doctor");
      sidebar.id = "menu-item1"; // Change ID if needed
    }
    document.body.classList.remove("expand-menu");

    document.body.classList.add("mini-sidebar");
  }, []);
  useEffect(() => {
    document.body.classList.remove("expand-menu");

    document.body.classList.add("mini-sidebar");
  }, []);
  const toast = message; // use antd message as toast

  function setKey(key, val) {
    setSettings((s) => ({ ...s, [key]: val }));
  }

  function setMargin(side, val) {
    setSettings((s) => ({ ...s, margins: { ...s.margins, [side]: val } }));
  }

  function handleDiscard() {
    setSettings({
      language: "English",
      fontFamily: "Arial, Helvetica, sans-serif",
      fontSize: 14,
      footerAlign: "right",
      template: "Single Column",
      medicineTemplate: "Table",
      investigationResultTemplate: "Table",
      investigationTemplate: "Numbered List",
      letterhead: false,
      signature: true,
      margins: { top: 2, right: 1.5, bottom: 2, left: 1.5 },
    });
    toast.info({ content: "Changes discarded" });
  }
  const fetchPrintSettings = async () => {
    setLoading(true);
    try {
      const result = await getPrintSettings(userData?.id);
      if (result.success && result.data) {
        const formData = result.data;

        setSettings((prev) => ({
          ...prev,
          margins: {
            top: formData.prescription_print_header_margin,
            right: formData.prescription_print_right_margin,
            bottom: formData.prescription_print_footer_margin,
            left: formData.prescription_print_left_margin,
          },
          signature: formData.doctor_signature_image_url || "",
        }));
      } else {
        console.error(
          "Error:",
          result.error || "Failed to fetch Print Settings"
        );
      }
    } catch (err) {
      console.error("Exception:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintSettings();
    setLoading(false);
  }, []);

  const handleSave = async () => {
    let form = {
      prescription_print_header_margin: settings.margins.top,
      prescription_print_footer_margin: settings.margins.bottom,
      prescription_print_left_margin: settings.margins.left,
      prescription_print_right_margin: settings.margins.right,
      doctor_signature_image: settings.signature,
    };
    try {
      const result = await addPrintSettings(form, userData?.id);

      // const result = await addpatient(formData)
      if (result?.success) {
        messageApi.success("Settings updated successfully!");
        // navigate("/patientslist")
        console.log("print settings :", result);
      } else {
        const errors = result?.error;
        Object.entries(errors).forEach(([field, messages]) => {
          const messageArray = Array.isArray(messages) ? messages : [messages];
          messageArray.forEach((msg) => {
            messageApi.error({
              content: `${field}: ${msg}`,
              style: {
                maxWidth: "300px",
                minWidth: "300px",
                textAlign: "left",
                wordBreak: "break-word",
              },
            });
          });
        });
      }
    } catch (error) {
      console.error("Error adding patient:", error?.message);
      messageApi.error("Failed to Update Settings");
    }
  };

  function handlePreview() {
    toast.open({
      content: "Preview available on-screen. Use browser print to print.",
    });
    // Could open print dialog if desired: window.print()
  }
  return (
    <div>
      <Sidebar activeClassName="print-settings" />
      <div className="page-wrapper-doctor">
        <div className="content">
          <div className="page-header">
            <b>
              <h3 className="page-title font-weight-bold">Doctor Schedule</h3>
            </b>
          </div>

          <div className="card">
            <div className="card-body d-flex">
              <Container fluid className="py-3">
                <Row className="g-3">
                  {/* Settings Panel */}
                  <Col md={4} className="border-end border-secondary">
                    <Card>
                      <Card.Header>
                        <Card.Title className="mb-0">
                          Prescription Template
                        </Card.Title>
                      </Card.Header>
                      <Card.Body>
                        <Form>
                          <Form.Label>Print A4 Margins (%) </Form.Label>
                          <Row className="g-2 mb-3">
                            <Col xs={6}>
                              <Form.Group>
                                <Form.Label className="small mb-1">
                                  Top
                                </Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.1"
                                  value={settings.margins.top}
                                  onChange={(e) =>
                                    setMargin(
                                      "top",
                                      Number(e.target.value || 0)
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col xs={6}>
                              <Form.Group>
                                <Form.Label className="small mb-1">
                                  Bottom
                                </Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.1"
                                  value={settings.margins.bottom}
                                  onChange={(e) =>
                                    setMargin(
                                      "bottom",
                                      Number(e.target.value || 0)
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col xs={6} className="mt-2">
                              <Form.Group>
                                <Form.Label className="small mb-1">
                                  Left
                                </Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.1"
                                  value={settings.margins.left}
                                  onChange={(e) =>
                                    setMargin(
                                      "left",
                                      Number(e.target.value || 0)
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col xs={6} className="mt-2">
                              <Form.Group>
                                <Form.Label className="small mb-1">
                                  Right
                                </Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.1"
                                  value={settings.margins.right}
                                  onChange={(e) =>
                                    setMargin(
                                      "right",
                                      Number(e.target.value || 0)
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Form.Group className="mt-4">
                            <Form.Label>Draw Signature</Form.Label>
                            <div
                              style={{
                                border: "2px dashed #aaa",
                                borderRadius: "8px",
                                width: "100%",
                                height: "150px",
                                background: "#fff",
                              }}
                            >
                              <SignatureCanvas
                                ref={sigCanvas}
                                penColor="black"
                                canvasProps={{
                                  width: 500,
                                  height: 150,
                                  className: "sigCanvas",
                                }}
                              />
                            </div>

                            <div className="mt-2 d-flex gap-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={clearSignature}
                              >
                                Clear
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={saveSignature}
                              >
                                Save
                              </Button>
                            </div>

                            {/* ✅ Preview Saved Signature */}
                            {settings.signature && (
                              <div className="mt-3 text-center">
                                <Form.Label>Saved Signature Preview</Form.Label>
                                <img
                                  src={settings.signature}
                                  alt="Signature"
                                  style={{
                                    border: "1px solid #ccc",
                                    borderRadius: "6px",
                                    maxWidth: "200px",
                                    width: "100%",
                                  }}
                                />
                              </div>
                            )}
                          </Form.Group>

                          {/* Language */}
                          {/* <Form.Group className="mb-3" controlId="language">
                  <Form.Label>Default Language</Form.Label>
                  <Form.Select
                    value={settings.language}
                    onChange={(e) => setKey("language", e.target.value)}
                  >
                    <option>English</option>
                    <option>বাংলা</option>
                    <option>Hindi</option>
                    <option>ગુજરાતી (Gujarati)</option>
                  </Form.Select>
                </Form.Group>

                <Row className="g-2">
                  <Col sm={7}>
                    <Form.Group controlId="fontFamily" className="mb-3">
                      <Form.Label>Font Family</Form.Label>
                      <Form.Select
                        value={settings.fontFamily}
                        onChange={(e) => setKey("fontFamily", e.target.value)}
                      >
                        <option value="Arial, Helvetica, sans-serif">Arial</option>
                        <option value="Georgia, 'Times New Roman', serif">Georgia</option>
                        <option value="'Times New Roman', Times, serif">Times New Roman</option>
                        <option value="system-ui, -apple-system, Segoe UI, Roboto, sans-serif">
                          System Sans
                        </option>
                        <option value="'Courier New', Courier, monospace">Courier New</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col sm={5}>
                    <Form.Group controlId="fontSize" className="mb-3">
                      <Form.Label>Font Size (px)</Form.Label>
                      <Form.Control
                        type="number"
                        min={10}
                        max={24}
                        value={settings.fontSize}
                        onChange={(e) => setKey("fontSize", Number(e.target.value || 14))}
                      />
                      <Form.Text className="text-muted">Size in px (10–24)</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Footer Alignment</Form.Label>
                  <div>
                    <Button
                      variant={settings.footerAlign === "left" ? "primary" : "outline-primary"}
                      className="me-2"
                      size="sm"
                      onClick={() => setKey("footerAlign", "left")}
                    >
                      Left
                    </Button>
                    <Button
                      variant={settings.footerAlign === "right" ? "primary" : "outline-primary"}
                      size="sm"
                      onClick={() => setKey("footerAlign", "right")}
                    >
                      Right
                    </Button>
                  </div>
                </Form.Group>

                <hr />

                <Form.Group className="mb-3">
                  <Form.Label>Prescription Template</Form.Label>
                  <Form.Select
                    value={settings.template}
                    onChange={(e) => setKey("template", e.target.value)}
                  >
                    <option>Single Column</option>
                    <option>Two Column</option>
                    <option>Compact</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Medicine Template</Form.Label>
                  <Form.Select
                    value={settings.medicineTemplate}
                    onChange={(e) => setKey("medicineTemplate", e.target.value)}
                  >
                    <option>Table</option>
                    <option>Bulleted List</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Investigation Result Template</Form.Label>
                  <Form.Select
                    value={settings.investigationResultTemplate}
                    onChange={(e) => setKey("investigationResultTemplate", e.target.value)}
                  >
                    <option>Table</option>
                    <option>Simple</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Investigation Template</Form.Label>
                  <Form.Select
                    value={settings.investigationTemplate}
                    onChange={(e) => setKey("investigationTemplate", e.target.value)}
                  >
                    <option>Numbered List</option>
                    <option>Checklist</option>
                  </Form.Select>
                </Form.Group>

                <hr />

                <Row className="g-2 align-items-center mb-3">
                  <Col xs={8}>
                    <div className="d-flex flex-column">
                      <Form.Label className="mb-0">Letterhead</Form.Label>
                      <small className="text-muted">Show practice header</small>
                    </div>
                  </Col>
                  <Col xs={4} className="text-end">
                    <Switch
                      checked={settings.letterhead}
                      onChange={(v) => setKey("letterhead", v)}
                    />
                  </Col>
                </Row>

                <Row className="g-2 align-items-center mb-3">
                  <Col xs={8}>
                    <div className="d-flex flex-column">
                      <Form.Label className="mb-0">Signature</Form.Label>
                      <small className="text-muted">Include signature block</small>
                    </div>
                  </Col>
                  <Col xs={4} className="text-end">
                    <Switch
                      checked={settings.signature}
                      onChange={(v) => setKey("signature", v)}
                    />
                  </Col>
                </Row> */}

                          <hr />

                          {/* Margins */}

                          {/* <div className="d-flex gap-2 justify-content-end mt-2">
                  <Button variant="outline-secondary" size="sm" onClick={handleDiscard}>
                    Discard Changes
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handleSave}>
                    Save Changes
                  </Button>
                  <Button onClick={handlePreview} size="sm">
                    Preview
                  </Button>
                </div> */}
                        </Form>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Preview */}
                  <Col md={8}>
                    <Card>
                      <Card.Header className="d-flex justify-content-between align-items-start border-bottom border-secondary">
                        <div>
                          <Card.Title className="mb-0">
                            Prescription Preview
                          </Card.Title>
                          <div className="text-muted small">
                            A4 aspect preview using your settings
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleDiscard}
                          >
                            Discard
                          </Button>
                          <Button
                            // variant="secondary"
                            size="sm"
                            onClick={handleSave}
                          >
                            Save
                          </Button>
                          {/* <Button size="sm" onClick={handlePreview}>
                            Preview
                          </Button> */}
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <PrescriptionPreview settings={settings} />
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Container>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PrescriptionPreview({ settings }) {
  const {
    language,
    fontFamily,
    fontSize,
    footerAlign,
    letterhead,
    signature,
    margins,
  } = settings;

  // Convert % margins into inches based on A4 paper width
  const pagePadding = `${margins.top}% ${margins.right}% ${margins.bottom}% ${margins.left}%`;

  return (
    <div
      style={{
        backgroundColor: "#ffffffff",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "30px 0",
        overflow: "auto",
      }}
    >
      <div
        style={{
          width: "8.27in", // A4 width
          height: "11.69in", // A4 height
          backgroundColor: "white",
          boxShadow: "0 0 15px rgba(0,0,0,0.2)",
          borderRadius: "6px",
          padding: pagePadding,
          fontFamily,
          fontSize: `${fontSize}px`,
          position: "relative",
          boxSizing: "border-box",
        }}
      >
        {/* Letterhead */}
        {letterhead && (
          <div className="text-center mb-3">
            <h4 className="fw-bold mb-1">Dr. Michael Ashford</h4>
            <div className="text-muted small">
              MBBS, MD (Internal Medicine) • Ashford Clinic • 123 Medical Avenue
            </div>
            <div className="text-muted small">+1 (555) 123-4567</div>
            <hr />
          </div>
        )}

        {/* Patient Info */}
        <div className="d-flex justify-content-between mb-3">
          <div>
            <div>
              <strong>Name:</strong> Alex Smith
            </div>
            <div>
              <strong>Age/Sex:</strong> 36 / M
            </div>
            <div>
              <strong>ID:</strong> P12345
            </div>
            <div>
              <strong>Address:</strong> 45 Hill Street, NY
            </div>
          </div>
          <div className="text-end">
            <div>
              <strong>Date:</strong> 15 Oct 2025
            </div>
            <div>
              <strong>Mobile:</strong> +1 (555) 987-6543
            </div>
          </div>
        </div>

        <hr />

        {/* Symptoms / Findings */}
        <div className="mb-3">
          <div>
            <strong>Symptoms:</strong> Headache, Fever, Fatigue, Body ache, Sore
            throat, Chills, Loss of appetite
          </div>
          <div>
            <strong>Findings:</strong> Mild dehydration, Clear chest, No focal
            deficit, Slightly elevated temperature, Normal blood pressure,
            Mildly pale conjunctiva
          </div>
          <div>
            <strong>Diagnosis:</strong> Suspected Viral Fever, Rule out Typhoid,
            Rule out Malaria, Mild Upper Respiratory Infection
          </div>
        </div>

        {/* Medicine Table */}
        <div className="mb-3">
          <strong>Medicine :</strong>
          <table
            className="table table-bordered mt-2"
            style={{ width: "100%", borderCollapse: "collapse" }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa" }}>
                <th style={{ border: "1px solid #dee2e6", padding: "4px" }}>
                  #
                </th>
                <th style={{ border: "1px solid #dee2e6", padding: "4px" }}>
                  Medicine
                </th>
                <th style={{ border: "1px solid #dee2e6", padding: "4px" }}>
                  Dosage
                </th>
                <th style={{ border: "1px solid #dee2e6", padding: "4px" }}>
                  Duration
                </th>
                <th style={{ border: "1px solid #dee2e6", padding: "4px" }}>
                  Note
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                {
                  med: "Tab. Paracetamol 500 mg",
                  dose: "1-1-1",
                  duration: "3 days",
                  note: "After meals",
                },
                {
                  med: "Cap. Omeprazole 20 mg",
                  dose: "1-0-0",
                  duration: "7 days",
                  note: "Before breakfast",
                },
                {
                  med: "ORS Solution",
                  dose: "200 ml each episode",
                  duration: "Till recovery",
                  note: "Max 1 L/day",
                },
              ].map((item, i) => (
                <tr key={i}>
                  <td
                    style={{
                      border: "1px solid #dee2e6",
                      textAlign: "center",
                      padding: "4px",
                    }}
                  >
                    {i + 1}
                  </td>
                  <td style={{ border: "1px solid #dee2e6", padding: "4px" }}>
                    {item.med}
                  </td>
                  <td style={{ border: "1px solid #dee2e6", padding: "4px" }}>
                    {item.dose}
                  </td>
                  <td style={{ border: "1px solid #dee2e6", padding: "4px" }}>
                    {item.duration}
                  </td>
                  <td style={{ border: "1px solid #dee2e6", padding: "4px" }}>
                    {item.note}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Advice Section */}
        <div className="mb-3">
          <div>
            <strong>Investigations:</strong> CBC, Rapid Malaria, COVID Test
          </div>
          <div>
            <strong>Advice:</strong> Rest, Fluids, Avoid spicy food
          </div>
          <div>
            <strong>Follow-up:</strong> After 3 days
          </div>
        </div>

        {/* <hr style={{ marginBottom: "80px" }}></hr> */}

        {/* Footer */}
        <div className="d-flex justify-content-between align-items-end ">
          <div className="text-center">
            <div className="fw-medium">Signature</div>
            <div className="text-muted small">(Doctor&apos;s Signature)</div>
          </div>
          <div className="text-end">
            {/* <div className="mb-3 fst-italic text-muted fs-5">
                  {doctor.last_name}
                </div> */}
            <div>
              {settings.signature && (
                <div
                  className="mt-2 p-2  text-center"
                  style={{ maxWidth: "200px" }}
                >
                  <img
                    src={settings.signature}
                    alt="Signature Preview"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                  {/* <small className="text-muted d-block mt-1">
                                  Signature Preview
                                </small> */}
                </div>
              )}
              <div className="fw-medium">Dr. Allen john</div>
              <div className="small text-muted">Consultant at NY, Yurop</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

PrescriptionPreview.propTypes = {
  settings: PropTypes.object.isRequired,
};
